import { Routes } from '@dtx-company/true-common/src/constants/routes'
import { decodeFronteggAccessToken } from '@dtx-company/ithaca-sdk/src/frontegg/decodeFronteggAccessToken'
import { useAuthState } from '@dtx-company/inter-app/src/hooks/useAuthState'
import { useEffect, useState } from 'react'
import { useFlowFeature } from '@dtx-company/inter-app/src/hooks/useFlowFeature/useFlowFeature'
import { useRouter } from 'next/router'

type EmailVerificationResult = 'SUCCESS' | 'ALREADY_VERIFIED' | 'FAILURE'
const emailVerificationResultMap: EmailVerificationResult[] = [
  'SUCCESS',
  'ALREADY_VERIFIED',
  'FAILURE'
]

interface UseBannerControllerState {
  shouldShowSuccessBanner: boolean
  shouldShowFailureBanner: boolean
  shouldShowPendingBanner: boolean
}

const useBannerController = (): UseBannerControllerState => {
  const { token } = useAuthState()
  const { query, isReady: isRouterReady, pathname } = useRouter()
  const [isEmailVerificationEnabled] = useFlowFeature('email-verification', {
    autoUpdate: true
  })
  const [isEmailVerified, setIsEmailVerified] = useState<boolean | undefined>(undefined)
  const [bannerQueryParam, setBannerQueryParam] = useState<
    EmailVerificationResult | false | undefined
  >(undefined)

  const lockBannerQueryParamEffect = (): void => {
    if (isEmailVerificationEnabled && bannerQueryParam === undefined && isRouterReady) {
      const result = query.emailVerificationResult
      const verifiedResult = emailVerificationResultMap.find(value => value === result)
      if (verifiedResult) setBannerQueryParam(verifiedResult)
      else setBannerQueryParam(false)
    }
  }
  useEffect(lockBannerQueryParamEffect, [
    bannerQueryParam,
    isEmailVerificationEnabled,
    isRouterReady,
    query.emailVerificationResult
  ])

  // todo swap w/ atlas
  // https://flowcode.slack.com/archives/C050W0XHJET/p1690215118912709?thread_ts=1684429578.195769&cid=C050W0XHJET
  const lockIsEmailVerifiedFromJWTEffect = (): void => {
    if (isEmailVerificationEnabled && isEmailVerified === undefined && token) {
      const decodedToken = decodeFronteggAccessToken(token)

      setIsEmailVerified(decodedToken?.email_verified ?? false)
    }
  }
  useEffect(lockIsEmailVerifiedFromJWTEffect, [isEmailVerificationEnabled, isEmailVerified, token])

  if (isEmailVerificationEnabled) {
    const isAuthButEmailUnverified = isEmailVerified === false
    const onAuthPage = pathname === Routes.SIGN_IN || pathname === Routes.SIGN_UP

    const shouldShowSuccessBanner =
      bannerQueryParam === 'SUCCESS' || bannerQueryParam === 'ALREADY_VERIFIED'
    const shouldShowFailureBanner = bannerQueryParam === 'FAILURE'
    const shouldShowPendingBanner =
      isAuthButEmailUnverified && isRouterReady && !bannerQueryParam && !onAuthPage

    return { shouldShowSuccessBanner, shouldShowFailureBanner, shouldShowPendingBanner }
  }

  return {
    shouldShowSuccessBanner: false,
    shouldShowFailureBanner: false,
    shouldShowPendingBanner: false
  }
}

export default useBannerController
